/**
 * @file ReturnStageEditDialog.tsx
 * @description Создание / редактирование стадии pipeline'а возвратов
 * (phase10.5 шаг 3). Зеркало `OrderStageEditDialog`.
 *
 * Особенности:
 *  - kinds — enum `ReturnStageKind` (REQUESTED / APPROVED / ... / REFUNDED / REJECTED);
 *  - менять kind можно ТОЛЬКО когда на стадии 0 возвратов;
 *  - isActive=false тоже требует 0 возвратов.
 */

import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useNotify } from "../ui/NotifyProvider";
import { SelectField } from "../ui/SelectField";
import { ApiError } from "../../lib/api-client";
import {
  RETURN_STAGE_KIND_LABEL,
  createReturnStage,
  updateReturnStage,
  type ReturnStageConfigDto,
  type ReturnStageKind,
} from "../../lib/api-returns";

interface Props {
  mode: "create" | "edit";
  pipelineId: string;
  stage?: ReturnStageConfigDto;
  onClose: () => void;
  onSaved: () => void;
}

const KINDS: ReturnStageKind[] = [
  "REQUESTED",
  "APPROVED",
  "AWAITING_GOODS",
  "INSPECTION",
  "APPROVED_REFUND",
  "REFUNDED",
  "REJECTED",
];

const PALETTE = [
  "#c95a4f",
  "#c0a890",
  "#9e8b5a",
  "#7d8c4f",
  "#5a6b36",
  "#3d4b1f",
  "#9e9483",
  "#fbecce",
] as const;
const DEFAULT_COLOR = PALETTE[0];

/** Опции выбора типа — из реестра подписей видов стадий возврата. */
const KIND_OPTIONS = KINDS.map((k) => ({ value: k, label: RETURN_STAGE_KIND_LABEL[k] }));

export function ReturnStageEditDialog({
  mode,
  pipelineId,
  stage,
  onClose,
  onSaved,
}: Props) {
  const notify = useNotify();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ReturnStageKind>("REQUESTED");
  const [color, setColor] = useState<string>(DEFAULT_COLOR);
  const [slaHours, setSlaHours] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const kindLocked = mode === "edit" && (stage?.returnsCount ?? 0) > 0;
  const activeLocked = mode === "edit" && (stage?.returnsCount ?? 0) > 0;

  useEffect(() => {
    setError(null);
    if (mode === "edit" && stage) {
      setCode(stage.code);
      setName(stage.name);
      setKind(stage.kind);
      setColor(stage.color ?? DEFAULT_COLOR);
      setSlaHours(stage.slaHours ? String(stage.slaHours) : "");
      setIsActive(stage.isActive);
    } else {
      setCode("");
      setName("");
      setKind("REQUESTED");
      setColor(DEFAULT_COLOR);
      setSlaHours("");
      setIsActive(true);
    }
  }, [mode, stage]);

  async function onSubmit() {
    setError(null);
    if (!code.trim()) {
      setError("Код стадии обязателен (латиница, цифры, _).");
      return;
    }
    if (!/^[a-z0-9_]+$/.test(code.trim())) {
      setError("Код: только латиница нижнего регистра, цифры и _.");
      return;
    }
    if (!name.trim()) {
      setError("Название обязательно.");
      return;
    }
    const slaNum = slaHours.trim() ? parseInt(slaHours, 10) : undefined;
    if (slaNum !== undefined && (isNaN(slaNum) || slaNum < 0)) {
      setError("SLA в часах — положительное число.");
      return;
    }

    setSaving(true);
    try {
      if (mode === "create") {
        await createReturnStage(pipelineId, {
          code: code.trim(),
          name: name.trim(),
          kind,
          color,
          slaHours: slaNum,
        });
      } else {
        await updateReturnStage(pipelineId, stage!.id, {
          code: code.trim(),
          name: name.trim(),
          ...(kindLocked ? {} : { kind }),
          color,
          slaHours: slaNum,
          ...(activeLocked ? {} : { isActive }),
        });
      }
      notify.success(mode === "create" ? "Стадия создана" : "Стадия обновлена");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Новая стадия возврата"
              : "Редактирование стадии возврата"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {error ? <Alert severity="error">{error}</Alert> : null}

          {/* На телефоне — столбиком: список типов не помещается в половину 360. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rs-code">Код</Label>
              <Input
                id="rs-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase())}
                placeholder="custom_step"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="rs-kind">Тип (автоматика)</Label>
              <SelectField
                id="rs-kind"
                value={kind}
                disabled={kindLocked}
                onChange={(next) => setKind(next as ReturnStageKind)}
                options={KIND_OPTIONS}
                ariaLabel="Тип стадии возврата"
              />
              {kindLocked ? (
                <span className="type-caption text-on-surface-variant">
                  Менять тип нельзя — на стадии есть возвраты.
                </span>
              ) : null}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rs-name">Название</Label>
            <Input
              id="rs-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Согласован к приёмке"
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Цвет</Label>
            <div className="flex flex-wrap gap-2">
              {PALETTE.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setColor(p)}
                  className={`size-7 rounded-full border-2 ${
                    color === p ? "border-on-surface" : "border-transparent"
                  }`}
                  style={{ backgroundColor: p }}
                  aria-label={`Цвет ${p}`}
                />
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rs-sla">SLA (часов до эскалации)</Label>
            <Input
              id="rs-sla"
              type="number"
              min={0}
              max={8760}
              value={slaHours}
              onChange={(e) => setSlaHours(e.target.value)}
              placeholder="240"
            />
            <span className="text-xs text-on-surface-variant">
              Типовые: согласование 48ч, проверка 72ч, возврат денег 240ч (10 дней — ЗоЗПП ст.22).
            </span>
          </div>

          {mode === "edit" ? (
            <label className="flex min-h-12 cursor-pointer items-center gap-2 text-sm text-on-surface-variant">
              {/* Общий Checkbox вместо нативного (UI-контракт). */}
              <Checkbox
                checked={isActive}
                disabled={activeLocked}
                onCheckedChange={(checked) => setIsActive(checked === true)}
              />
              Активна
              {activeLocked ? (
                <span className="type-caption text-on-surface-variant">
                  (нельзя архивировать — есть возвраты)
                </span>
              ) : null}
            </label>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={saving}>
            {saving ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
